import { motion } from "framer-motion";
import { Music } from "lucide-react";
import React from "react";
import { useFullscreen } from "../../hooks/useFullscreen";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { usePlayerStore } from "../../stores/playerStore"; // Step 3.1: Import usePlayerStore
import SpectrumVisualizer from "../SpectrumVisualizer"; // Step 3.1: Import SpectrumVisualizer
import { PlayerControls } from "./PlayerControls";
import { PlayerHeader } from "./PlayerHeader";
import { PlayerProgress } from "./PlayerProgress";

interface ExpandedPlayerProps {
  title: string;
  artist: string;
  albumArt?: string;
  isPlaying: boolean;
  progress: number;
  volume: number;
  duration: number;
  loopMode: "off" | "one" | "all";
  isLiked: boolean;
  isHapticsSupported: boolean;
  isHapticsEnabled: boolean;
  hideHeader?: boolean;
  isDragging: boolean;
  dragProgress: number | null;
  onMinimize: () => void;
  onPlayPause: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onLoopChange: (mode: "off" | "one" | "all") => void;
  onVolumeChange: (value: number) => void;
  onLikeToggle: () => void;
  onShare: () => void;
  onHapticsToggle: () => void;
  onSeekStart: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void;
  onSeek: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void;
  onSeekEnd: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void;
  onAddToPlaylist: () => void;
  onAddToAlbum: () => void;
  showHapticPresets: boolean;
  trackId: string;
}

export const ExpandedPlayer: React.FC<ExpandedPlayerProps> = ({
  title,
  artist,
  albumArt,
  isPlaying,
  progress,
  volume,
  duration,
  loopMode,
  isLiked,
  isHapticsSupported,
  isHapticsEnabled,
  hideHeader,
  isDragging,
  dragProgress,
  onMinimize,
  onPlayPause,
  onNext,
  onPrevious,
  onLoopChange,
  onVolumeChange,
  onLikeToggle,
  onShare,
  onHapticsToggle,
  onSeekStart,
  onSeek,
  onSeekEnd,
  onAddToPlaylist,
  onAddToAlbum,
  showHapticPresets,
  trackId,
}) => {
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const isSmallScreen = useMediaQuery("(max-width: 640px)");
  const { visualizerAnalyserNode } = usePlayerStore(); // Step 3.2: Get AnalyserNode from store

  return (
    <div className={`w-full h-full mx-auto overflow-y-auto ${isFullscreen ? "fixed inset-0 z-50" : ""}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: showHapticPresets ? 0.7 : 1,
          translateY: showHapticPresets ? -100 : 0,
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
        className={`h-full flex flex-col justify-between mx-auto relative z-10 p-4 md:p-8 ${
          isFullscreen ? "max-w-full" : "max-w-full"
        }`}
        style={{
          backdropFilter: isFullscreen ? "blur(10px)" : undefined,
          backgroundColor: isFullscreen ? "rgba(0, 0, 0, 0.5)" : undefined,
        }}
      >
        {!hideHeader && (
          <PlayerHeader onMinimize={onMinimize} onToggleFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />
        )}

        <div className="flex flex-col items-center justify-center flex-grow">
          <motion.div
            layoutId="albumArt"
            className={`relative h-[40vh] aspect-square mb-8`}
            style={{
              maxWidth: "min(100%, 40vh)",
              width: "40vh",
            }}
          >
            {albumArt ? (
              <img src={albumArt} alt={title} className="w-full h-full object-cover rounded-lg shadow-2xl" />
            ) : (
              <div className="w-full h-full bg-zinc-800 rounded-lg flex items-center justify-center">
                <Music className="w-32 h-32 text-zinc-600" />
              </div>
            )}
          </motion.div>

          <div className="text-center mb-8 w-full max-w-[90%]">
            <a href={`/track/${trackId}`}>
              <h2 className="text-2xl font-bold text-white mb-2 w-full truncate hover:underline">{title}</h2>
            </a>
            <p className="text-lg text-gray-300 truncate">{artist}</p>
          </div>
        </div>
        
        {/* Step 3.3: Render SpectrumVisualizer */}
        <div className="my-4 md:my-6 flex justify-center px-4">
          <SpectrumVisualizer 
            analyserNode={visualizerAnalyserNode} 
            width={isSmallScreen ? 280 : 360} // Adjusted width for better fit
            height={isSmallScreen ? 60 : 100} // Adjusted height
            barColor="#FACC15" // Tailwind yellow-500
            barGap={2}
          />
        </div>

        <div className="w-full max-w-full mx-auto">
          <PlayerControls
            isPlaying={isPlaying}
            loopMode={loopMode}
            onPlayPause={onPlayPause}
            onNext={onNext}
            onPrevious={onPrevious}
            onLoopChange={onLoopChange}
            isLiked={isLiked}
            volume={volume}
            onVolumeChange={onVolumeChange}
            onLikeToggle={onLikeToggle}
            onShare={onShare}
            isHapticsSupported={isHapticsSupported}
            isHapticsEnabled={isHapticsEnabled}
            onHapticsToggle={onHapticsToggle}
            onAddToPlaylist={onAddToPlaylist}
            onAddToAlbum={onAddToAlbum}
          />

          <PlayerProgress
            progress={progress}
            duration={duration}
            isDragging={isDragging}
            dragProgress={dragProgress}
            onSeekStart={onSeekStart}
            onSeek={onSeek}
            onSeekEnd={onSeekEnd}
          />
        </div>
      </motion.div>
    </div>
  );
};
